import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Period } from '@spike/model';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { fetchServiceSessionsThunk } from 'actions/serviceSessions/ServiceSessionsActions';
import { AlertMessage2 } from 'components/AlertMessage2';
import { useTimeZone } from 'hooks';
import { Service } from 'model/Service';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { getServiceSessionConfig, SessionBusinessArea } from 'model/ServiceSession';
import { MultiSlotDrawer } from '../MultiSlotDrawer/MultiSlotDrawer';
import { ScheduleTypeCalendarToolbar } from '../ScheduleTypeCalendar/ScheduleTypeCalendarToolbar';
import { SessionAddSlotDrawer } from './SessionAddSlotDrawer';
import { SessionScheduleGrid } from './SessionScheduleGrid';
import { SessionSetupDefaultDrawer } from './SessionSetupDefaultDrawer';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: 0,
            minWidth: '100%',
            overflowX: 'hidden',
            marginTop: 24
        },
        alertWrap: {
            maxWidth: 840,
            marginBottom: 24,

            [theme.breakpoints.up('md')]: {
                padding: '0px 40px'
            }
        },
        alert: {
            '&&': {
                marginBottom: 0
            },

            '& p': {
                fontSize: 16,
                lineHeight: 1.4
            }
        }
    })
);

export interface SessionScheduleTypeProps {
    area: SessionBusinessArea;
}

export const SessionScheduleType: React.FC<SessionScheduleTypeProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const timeZone = useTimeZone();

    const services = useSelector((state: RootState) => state.services.servicesByBusinessArea[props.area].services);
    const staff = useSelector((state: RootState) => state.staff.staff);

    const [period, setPeriod] = React.useState<Period>(() => ({
        from: moment().tz(timeZone).startOf('week'),
        to: moment().tz(timeZone).endOf('week')
    }));

    const [showDefaultDrawer, setShowDefaultDrawer] = React.useState(false);
    const [zonePanelOpen, setZonePanelOpen] = React.useState(false);
    const [selectedSlot, setSelectedSlot] = React.useState<{ service: Service; date: Moment }>();

    React.useEffect(() => {
        dispatch(fetchServicesThunk(undefined, 1, 100, 'active', undefined, '', props.area));
        dispatch(fetchStaffThunk());
    }, []);

    React.useEffect(() => {
        dispatch(fetchServiceSessionsThunk(props.area, period.from.format('YYYY-MM-DD')));
    }, [period.from]);

    const sessionServices = React.useMemo(
        () => services.filter(service => getServiceSessionConfig(service, props.area).usesSessions),
        [services]
    );

    const activeStaff = React.useMemo(() => staff.filter(member => member.active), [staff]);

    const dates: Array<Moment> = React.useMemo(
        () => Array.from({ length: 7 }, (_, index) => period.from.clone().add(index, 'days')),
        [period.from]
    );

    const closeDrawersHandler = () => {
        setZonePanelOpen(false);
        setShowDefaultDrawer(false);
        setSelectedSlot(undefined);
    };

    const goToTodayHandler = () => {
        setPeriod({
            from: moment().tz(timeZone).startOf('week'),
            to: moment().tz(timeZone).endOf('week')
        });
    };

    return (
        <div className={classes.root}>
            <div className={classes.alertWrap}>
                <AlertMessage2
                    icon={faCircleInfo}
                    bgColor="#F8F5F1"
                    bdColor="#BCB8AE"
                    className={classes.alert}
                >
                    <Typography>Private Sessions in facility will be based on each staff member’s schedule</Typography>
                </AlertMessage2>
            </div>

            <ScheduleTypeCalendarToolbar
                selectedWeek={period}
                onChange={setPeriod}
                onGoToToday={goToTodayHandler}
                onSetupDefaultSlots={() => setShowDefaultDrawer(true)}
            />

            <SessionScheduleGrid
                area={props.area}
                week={period}
                services={sessionServices}
                onAddSlot={(service, date) => setSelectedSlot({ service, date })}
            />

            <MultiSlotDrawer
                open={showDefaultDrawer}
                hideBackdrop={zonePanelOpen}
                onClose={closeDrawersHandler}
            >
                {showDefaultDrawer && (
                    <SessionSetupDefaultDrawer
                        area={props.area}
                        staff={activeStaff}
                        services={sessionServices}
                        onZonePanelChange={setZonePanelOpen}
                        onClose={closeDrawersHandler}
                    />
                )}
            </MultiSlotDrawer>

            <MultiSlotDrawer
                open={!!selectedSlot}
                hideBackdrop={zonePanelOpen}
                onClose={closeDrawersHandler}
            >
                {selectedSlot && (
                    <SessionAddSlotDrawer
                        // Remounts per cell so the drawer never reopens on the previously clicked date.
                        key={`${selectedSlot.service.id}-${selectedSlot.date.format('YYYY-MM-DD')}`}
                        area={props.area}
                        dates={dates}
                        staff={activeStaff}
                        date={selectedSlot.date}
                        service={selectedSlot.service}
                        onZonePanelChange={setZonePanelOpen}
                        onClose={closeDrawersHandler}
                    />
                )}
            </MultiSlotDrawer>
        </div>
    );
};
